import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { MessageSquareMore, Plus } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { FORM_CONTROL_FOCUS_CLASS } from '@/components/ui/form-control-styles'
import { toast } from '@/components/ui/toast'
import { getApiErrorMessage } from '@/services/api'
import { useAddTechnicianNote } from '@/hooks/useTechnicians'
import { cn } from '@/lib/utils'

interface Props {
  technicianId: string
  notes?: string | null
}

const cardClass =
  'overflow-hidden rounded-2xl border border-[#e7e9ef] bg-white shadow-[0_1px_4px_-4px_rgba(38,45,66,0.05),0_8px_16px_-4px_rgba(38,45,66,0.1)]'

type ParsedNote = {
  meta: string
  body: string
}

function parseTechnicianNotes(notes: string | null | undefined): ParsedNote[] {
  if (!notes?.trim()) return []

  const entries: ParsedNote[] = []
  let current: ParsedNote | null = null

  for (const line of notes.split('\n')) {
    const match = line.match(
      /^\[(\d{2}\/\d{2}\/\d{4} \d{2}:\d{2} - .+?)\] (.*)$/
    )
    if (match) {
      if (current) entries.push(current)
      current = { meta: match[1], body: match[2] }
      continue
    }

    if (current) {
      current.body = current.body ? `${current.body}\n${line}` : line
    } else if (line.trim()) {
      current = { meta: '', body: line }
    }
  }

  if (current) entries.push(current)
  return entries
}

// 2.10.6 (feedback #82) — notes card matching the technician detail mockups.
export function NotesTab({ technicianId, notes }: Props) {
  const { t } = useTranslation('technicians')
  const addNote = useAddTechnicianNote(technicianId)
  const [value, setValue] = useState('')
  const parsedNotes = parseTechnicianNotes(notes)

  const onSave = async () => {
    const note = value.trim()
    if (!note) return
    try {
      await addNote.mutateAsync(note)
      setValue('')
      toast({ variant: 'success', title: t('detail.noteAdded') })
    } catch (e) {
      toast({
        variant: 'destructive',
        title: getApiErrorMessage(e, t('detail.noteAddError')),
      })
    }
  }

  return (
    <section className={cardClass}>
      <div className='flex items-center gap-3 px-6 pb-5 pt-6'>
        <div className='flex size-8 shrink-0 items-center justify-center rounded-lg bg-[#fff1ea] text-[#ff5a1f]'>
          <MessageSquareMore className='h-4 w-4' />
        </div>
        <div className='min-w-0'>
          <h3 className='font-heading text-sm font-semibold leading-[1.2] text-[#171a20]'>
            {t('detail.internalNotes')}
          </h3>
        </div>
      </div>

      <div className='flex min-h-0 flex-col'>
        <div className='flex max-h-[336px] min-h-0 flex-col gap-2 overflow-y-auto px-6 pb-4 pt-1'>
          {parsedNotes.length === 0 ? (
            <p className='text-sm leading-[1.4] text-[#3d434e]'>
              {t('detail.noNotes')}
            </p>
          ) : (
            parsedNotes.map((note, index) => (
              <div
                key={index}
                className='flex min-h-20 flex-col gap-2 rounded-xl bg-[#f4f5f8] px-4 py-3'
              >
                {note.meta ? (
                  <p className='text-xs font-medium leading-4 text-[#3d434e]'>
                    {note.meta}
                  </p>
                ) : null}
                <div className='whitespace-pre-wrap wrap-break-word text-sm font-normal leading-[1.4] text-[#171a20]'>
                  {note.body}
                </div>
              </div>
            ))
          )}
        </div>

        <div className='mt-auto flex shrink-0 items-start gap-4 px-6 pb-6 pt-4'>
          <Textarea
            value={value}
            maxLength={2000}
            rows={1}
            placeholder={t('detail.notePlaceholder')}
            onChange={e => setValue(e.target.value)}
            className={cn(
              'min-h-10 flex-1 resize-y rounded-xl border-[#e7e9ef] bg-[#f4f5f8] px-4 py-2.5 text-sm font-normal leading-[1.4] text-[#171a20] placeholder:font-normal placeholder:text-[#6c7285]',
              FORM_CONTROL_FOCUS_CLASS
            )}
          />

          <Button
            type='button'
            variant='ghost'
            disabled={!value.trim() || addNote.isPending}
            onClick={onSave}
            className='h-10 shrink-0 gap-2 rounded-xl px-3 py-3 text-sm font-medium leading-none text-[#ff5a1f] hover:bg-[#fff1ea] hover:text-[#ff5a1f]'
          >
            <Plus className='size-4' />
            {t('detail.addNote')}
          </Button>
        </div>
      </div>
    </section>
  )
}

export default NotesTab
